import SearchInput, { SearchInputStyles } from "@aus/Overrides/Widgets/Header/SearchInput";
import { AusState } from "@aus/Store/AusState";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { SyntheticEvent } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    backgroundImage = "backgroundImage",
    desiredHeight = "desiredHeight",
    backupBackgroundColor = "backupBackgroundColor",
}

const mapStateToProps = (state: AusState) => {
    return {
        isSecondaryHeader: state.headerPage.isSecondaryHeader,
    };
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.backgroundImage]: string;
        [fields.desiredHeight]: number;
        [fields.backupBackgroundColor]: string;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface SearchBannerStyles {
    container?: InjectableCss;
    searchInputStyles?: SearchInputStyles;
    abrevSearchInputContainer?: InjectableCss;
    abrevSearchHeadlineTextStyle?: InjectableCss;
    abrevContainerStyles?: InjectableCss;
    abrevSearchInput?: InjectableCss;
    searchContainer?: InjectableCss;
    searchOverlay?: InjectableCss;
}

export const searchBannerStyles: SearchBannerStyles = {
    abrevSearchInputContainer: {
        css: css`
            width: 80%;
            display: flex;
            align-items: center;
        `,
    },

    abrevSearchHeadlineTextStyle: {
        css: css`
            height: auto;
        `,
    },

    abrevContainerStyles: {
        css: css`
            display: flex;
            justify-content: center;
        `,
    },

    abrevSearchInput: {
        css: css`
            flex: 0 4 100%;
        `,
    },
    searchContainer: {
        css: css`
            margin-left: -15px;
            margin-right: -15px;
            margin-top: -16px;
            position: relative;
            overflow: hidden;
        `,
    },
    searchOverlay: {
        css: css`
            position: relative !important;
            width: 70%;
            margin: auto;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            width: 95%;
                            margin: auto;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
};

const styles = searchBannerStyles;

const SearchBanner: React.FC<Props> = ({ id, fields, isSecondaryHeader }) => {
    const desiredHeightStyles = `height: ${fields.desiredHeight}px;`;
    const desiredHeightStylesCss = {
        css: css`
            ${desiredHeightStyles}
        `,
    };
    const backgroundImageStyles = `background-color: ${fields.backupBackgroundColor}; background-image: url(${fields.backgroundImage}); background-size: cover; background-position: center;`;
    const searchContainerStyles = {
        css: css`
            ${styles.searchContainer?.css || ""}
            ${desiredHeightStyles}
            display: flex;
            justify-content: center;
            align-items: center;
            ${backgroundImageStyles}
        `,
    };
    let searchOverlayStyles = {
        css: css`
            ${styles.searchOverlay?.css || ""}
            display: flex;
            justify-content: center;
            align-items: center;
            opacity: 1;
            visibility: visible;
            transition: visibility 1s, opacity 1s;
        `,
    };
    if (isSecondaryHeader) {
        searchOverlayStyles = {
            css: css`
                ${styles.searchOverlay?.css || ""}
                display: flex;
                justify-content: center;
                align-items: center;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            null,
                            css`
                                opacity: 0;
                                visibility: hidden;
                                transition: visibility 1s, opacity 1s;
                            `,
                        ],
                        "min",
                    )}
            `,
        };
    }

    return (
        <StyledWrapper {...desiredHeightStylesCss}>
            <StyledWrapper {...searchContainerStyles}>
                <StyledWrapper className="customSearchBar" {...searchOverlayStyles}>
                    <SearchInput id={id} extendedStyles={styles.searchInputStyles} placeholderText="" />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const searchBanner: WidgetModule = {
    component: connect(mapStateToProps)(SearchBanner),
    definition: {
        group: "Basic",
        icon: "search",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.backgroundImage,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: fields.desiredHeight,
                displayName: "Desired Banner Height",
                editorTemplate: "IntegerField",
                defaultValue: 100,
                tooltip: "Desired height in pixels",
            },
            {
                fieldType: "General",
                name: fields.backupBackgroundColor,
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                isRequired: true,
            },
        ],
    },
};

export default searchBanner;
